import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiEdit, FiPackage, FiTrash2 } from "react-icons/fi";
import { MdAssignmentAdd } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import HelpVideoButton from "../../components/HelpVideoButton";
import ProductCreateModal from "../../components/modal/ProductCreateModal";
import ProductLowStockModal from "../../components/modal/ProductLowStockModal";
import ProductReplanishModal from "../../components/modal/ProductReplanishModal";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { TextFeldUserSearch } from "../users/Users.styles";
import {
  ActionButton,
  Container,
  ProductSearchCard,
  Table,
  TableContainer,
  Title
} from "./product.styled";

export const Product = () => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalReplenishOpen, setIsModalReplenishOpen] = useState(false);
  const [isLowStockOpen, setIsLowStockOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [productToDelete, setProductToDelete] = useState<number | null>(null);
  const [userNameToDelete, setProductNameToDelete] = useState<any>(null);

  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [search, setSearch] = useState<string>("");

  const { isAdmin, isUser } = useAuth();

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  const containerId = "toaster-product-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const fetchProducts = async (currentPage: number) => {
    try {
      const response = await api.get(`/products`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          company_id: selectedCompany ? selectedCompany.company_id : undefined
        }
      });

      setProducts(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao buscar usuários:", error);
    }
  };

  const openDeleteDialog = (product: any) => {
    setProductToDelete(product.product_id);
    setDialogOpen(true);
    setProductNameToDelete(product.product_name);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setProductToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (productToDelete !== null) {
      try {
        await api.delete(`/products/${productToDelete}`);
        autoSuccess("Produto deletado com sucesso!");

        fetchProducts(page);
      } catch (error) {
        const msg = extractErrorMessage(error);
        autoError(msg);
        console.error("Erro ao deletar produto:", error);
      } finally {
        setDialogOpen(false);
        setProductToDelete(null);
      }
    }
  };

  const handleEdit = async (product_id: number) => {
    try {
      const response = await api.get(`/products/${product_id}`);
      setSelectedProduct(response.data);
      setIsModalOpen(true);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar usuário:", error);
      autoError(msg);
    }
  };

  const handReplenishProduct = async (product_id: number) => {
    try {
      const response = await api.get(`/products/${product_id}`);
      setSelectedProduct(response.data);
      setIsModalReplenishOpen(true);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar usuário:", error);
      autoError(msg);
    }
  };

  useEffect(() => {
    fetchProducts(page);
  }, [page, search, selectedCompany]);

  // A sidebar manda `?lowStock=1` ao clicar no sino: já abre a lista de
  // estoque baixo e limpa o parâmetro da URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lowStock") === "1") {
      setIsLowStockOpen(true);
      navigate("/product", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";

    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Produtos
        <CreateButton
          onClick={() => setIsModalOpen(true)}
          style={{ marginLeft: "14px" }}
        >
          <FiPackage size={18} /> Criar Produto
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/BZBXngqgogQ"
          modalTitle="Gerenciar produtos"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <ProductSearchCard>
        <TextFeldUserSearch
          id="user_search"
          label="Dados do Produto"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        {isAdmin && (
          <Autocomplete
            sx={{ width: 400 }}
            options={companies}
            getOptionLabel={(opt) => opt.company_name || ""}
            value={selectedCompany}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      </ProductSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              {/* <th>ID</th> */}
              <th>Nome</th>
              <th>Quantidade</th>
              <th>Código de barras</th>
              <th>Preço</th>
              <th>Localização</th>
              {/* <th>Status</th>

              <th>Criado em</th> */}
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product: ProductInterface) => (
              <tr key={product.product_id}>
                {/* <td>{product.product_id}</td> */}
                <td>{product.product_name}</td>
                <td>{product.product_quantity}</td>
                <td>{product.product_barcode}</td>
                <td>
                  {Number(product.product_unit_price).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  })}
                </td>
                <td>
                  {!product.product_location ||
                  product.product_location === "Não especificado"
                    ? "-"
                    : product.product_location}
                </td>

                <td>
                  <CustomTooltip title="Editar produto">
                    <ActionButton
                      onClick={() => handleEdit(product.product_id)}
                    >
                      <FiEdit size={20} color="#4caf50" />
                    </ActionButton>
                  </CustomTooltip>

                  <CustomTooltip title="Reabastecer produto">
                    <ActionButton
                      onClick={() => handReplenishProduct(product.product_id)}
                    >
                      <MdAssignmentAdd size={20} color="#08f" />
                    </ActionButton>
                  </CustomTooltip>

                  {!isUser && (
                    <CustomTooltip title="Excluir produto">
                      <ActionButton onClick={() => openDeleteDialog(product)}>
                        <FiTrash2 size={20} color="#f44336" />
                      </ActionButton>
                    </CustomTooltip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir {userNameToDelete}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <ProductCreateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        onProductCreated={() => fetchProducts(page)} // Atualiza a lista de usuários
        product={selectedProduct}
      />

      <ProductReplanishModal
        open={isModalReplenishOpen}
        onClose={() => {
          setIsModalReplenishOpen(false);
          setSelectedProduct(null);
        }}
        onProductCreated={() => fetchProducts(page)}
        product={selectedProduct}
      />

      <ProductLowStockModal
        open={isLowStockOpen}
        onClose={() => setIsLowStockOpen(false)}
      />
    </Container>
  );
};
